import { z } from 'zod';
import { MAX_QUESTIONS_PER_GENERATION } from '../entitlements';

export const quizQuestionSchema = z.object({
  id: z.string().uuid(),
  question: z.string().trim().min(1).max(300),
  type: z.enum(['single_choice', 'multiple_choice']),
  options: z.array(z.string().trim().min(1).max(300)).min(2).max(6),
  correctIndexes: z.array(z.number().int().min(0)).min(1),
});

export const quizSpecSchema = z.object({
  title: z.string().trim().min(1).max(140),
  questions: z.array(quizQuestionSchema).min(1).max(MAX_QUESTIONS_PER_GENERATION),
});

/**
 * Payload of a `bank_batch` artifact held as a reviewable draft.
 * Mirrors apps/api/src/modules/learn-artifacts/schemas/bank-draft.schema.ts —
 * keep the two in step, the API validates every write against its copy.
 */
/**
 * Optional, and tolerant of `null` on the way back out of Mongo.
 *
 * A question carries only the fields its type uses; the rest are `undefined`
 * when the draft is built. The driver serialises `undefined` to `null`, so a
 * plain `.optional()` rejects the very payload the API just validated and
 * stored. Mirrors the API helper of the same name.
 */
const optional = <T extends z.ZodTypeAny>(schema: T) =>
  schema.nullish().transform((value) => value ?? undefined);

const blankSlotSchema = z.object({
  id: z.string().min(1),
  position: z.number().int().min(0),
  acceptedAnswers: z.array(z.string()).default([]),
});

export const bankDraftQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.enum(['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE', 'SHORT_ANSWER', 'MATCHING', 'FILL_IN_BLANKS', 'OPEN_ENDED', 'REORDER', 'PASSAGE']),
  prompt: z.string().trim().min(1).max(20_000),
  options: optional(z.array(z.object({ id: z.string().min(1), value: z.string() }))),
  matchPairs: optional(z.array(z.object({ key: z.string(), value: z.string() }))),
  correctAnswer: z
    .union([z.string(), z.array(z.string()), z.record(z.string())])
    .nullish()
    .transform((value) => value ?? ''),
  points: z.number().min(0).max(1000).default(1),
  explanation: optional(z.string().max(20_000)),
  difficulty: optional(z.enum(['easy', 'medium', 'hard'])),

  answerMode: optional(z.enum(['exact', 'manual'])),
  acceptedAnswers: optional(z.array(z.string())),
  matchingOptions: optional(z.array(z.string())),
  maxWords: optional(z.number().int().min(1).max(1000)),
  mediaUrl: optional(z.string()),
  aiImageDescription: optional(z.string().max(2000)),
  rubric: optional(z.string().max(20_000)),
  blanks: optional(z.array(blankSlotSchema)),
  reorderItems: optional(z.array(z.object({ id: z.string().min(1), content: z.string() }))),
  correctOrder: optional(z.array(z.string())),
  passageText: optional(z.string().max(20_000)),
  passageTitle: optional(z.string()),
  subQuestions: optional(z.array(z.record(z.unknown()))),
});

export const bankDraftSchema = z.object({
  questions: z.array(bankDraftQuestionSchema).min(1).max(MAX_QUESTIONS_PER_GENERATION),
});

export type BankDraftQuestion = z.infer<typeof bankDraftQuestionSchema>;
export type BankDraft = z.infer<typeof bankDraftSchema>;

/** Null when the artifact carries no reviewable draft (e.g. a legacy pre-draft batch). */
export const parseBankDraft = (payload: unknown): BankDraft | null => {
  const validation = bankDraftSchema.safeParse(payload);
  return validation.success ? validation.data : null;
};

export interface RejectedDraftQuestion {
  /** Position in the stored payload, 0-based. */
  index: number;
  /** `field: message` per failing field, ready to show or log. */
  issues: string[];
  /** The raw stored question, for diagnosis. */
  raw: unknown;
}

export interface ParsedBankDraft {
  questions: BankDraftQuestion[];
  rejected: RejectedDraftQuestion[];
}

/**
 * Parses a draft question-by-question instead of all-or-nothing.
 *
 * This client schema is a MIRROR of the API's, so any drift between them is a
 * question the client can't read. Validating the whole array at once turns one
 * unreadable question into an empty dialog and a lost batch — the author sees a
 * blank panel and 10 perfectly good questions are unreachable. Failing per
 * question keeps the readable ones usable and names the rest.
 */
export const parseBankDraftQuestions = (payload: unknown): ParsedBankDraft => {
  const raw = (payload as { questions?: unknown[] } | undefined)?.questions;
  if (!Array.isArray(raw)) return { questions: [], rejected: [] };

  const questions: BankDraftQuestion[] = [];
  const rejected: RejectedDraftQuestion[] = [];

  raw.forEach((item, index) => {
    const result = bankDraftQuestionSchema.safeParse(item);
    if (result.success) {
      questions.push(result.data);
    } else {
      rejected.push({
        index,
        issues: result.error.issues.map(
          (issue) => `${issue.path.join('.') || 'root'}: ${issue.message}`,
        ),
        raw: item,
      });
    }
  });

  return { questions, rejected };
};

/**
 * Quiz drafts (2 types, plain text) shown through the same review dialog as bank
 * drafts, so "Review" means one thing everywhere.
 *
 * The mapping is lossless in BOTH directions as long as the dialog keeps quiz
 * questions on plain-text inputs: quizSpec stores options as bare strings with a
 * `correctIndexes` array and has no place for points, difficulty, explanation or
 * the other nine question types. The dialog disables exactly those controls for
 * quiz drafts rather than letting an author enter data that would be dropped on
 * save.
 */
export const quizSpecToDraft = (spec: QuizSpec): BankDraftQuestion[] =>
  spec.questions.map((q) => ({
    id: q.id,
    type: q.type === 'single_choice' ? 'MULTIPLE_CHOICE' : 'MULTIPLE_RESPONSE',
    prompt: q.question,
    options: q.options.map((value, index) => ({ id: `opt_${index}`, value })),
    correctAnswer:
      q.type === 'single_choice'
        ? `opt_${q.correctIndexes[0] ?? 0}`
        : q.correctIndexes.map((i) => `opt_${i}`),
    points: 1,
  }));

export const draftToQuizSpec = (title: string, questions: BankDraftQuestion[]): QuizSpec => ({
  title,
  questions: questions.map((q) => {
    const options = q.options ?? [];
    const answers = Array.isArray(q.correctAnswer)
      ? q.correctAnswer
      : [q.correctAnswer].filter((a): a is string => typeof a === 'string');
    const correctIndexes = options
      .map((opt, index) => (answers.includes(opt.id) ? index : -1))
      .filter((index) => index >= 0);

    return {
      id: q.id,
      question: q.prompt,
      type: q.type === 'MULTIPLE_RESPONSE' ? ('multiple_choice' as const) : ('single_choice' as const),
      options: options.map((opt) => opt.value),
      // Missing keys stay explicit so the author selects an answer before saving.
      correctIndexes,
    };
  }),
});

export const learnArtifactSchema = z.object({
  id: z.string().min(1),
  documentId: z.string().min(1),
  type: z.enum(['quiz', 'bank_batch']),
  status: z.enum(['generating', 'ready', 'failed']),
  title: z.string().trim().min(1),
  payload: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional(),
  errorMessage: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
});

export const learnArtifactsListSchema = z.array(learnArtifactSchema);

export type QuizQuestion = z.infer<typeof quizQuestionSchema>;
export type QuizSpec = z.infer<typeof quizSpecSchema>;
export type LearnArtifact = z.infer<typeof learnArtifactSchema>;


export const parseQuizPayload = (payload: unknown): QuizSpec | null => {
  const validation = quizSpecSchema.safeParse(payload);
  return validation.success ? validation.data : null;
};
